import { isPlatformServer } from '@angular/common';
import { inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import type { TranslateService } from '@ngx-translate/core';
import type { Request } from 'express';

import type { WrikeDynamicBaseHrefModuleConfiguration } from './types';
import { REQUEST } from '../../../express.tokens';

@Injectable()
export class WrikeDynamicBaseHrefService {
    private readonly injector = inject(Injector);
    private readonly platformId = inject(PLATFORM_ID);

    private config: WrikeDynamicBaseHrefModuleConfiguration;
    private translateService: TranslateService;

    public generateBase() {
        const bases = this.config.availableBases || [];
        const locales = this.config.availableLocales || [];
        const path = this.getCurrentPath();
        let currentLocale = this.config.defaultLocale || 'en';
        let base = '';

        const parsedUrl = path.split('/');
        if (parsedUrl.length) {
            let firstSegment = parsedUrl[1];

            if (locales.indexOf(firstSegment) > -1) {
                currentLocale = firstSegment;
                base += this.localeToSlug(currentLocale);
                firstSegment = parsedUrl[2] || '';
            }

            this.translateService.use(currentLocale);
            this.translateService.setFallbackLang(this.config.defaultLocale);

            if (bases.indexOf(`/${firstSegment}/`) > -1) {
                base += `/${firstSegment}`;
            }
        }

        this.setGlobalBasePathVariable(base);

        return base;
    }

    private localeToSlug(locale: string) {
        return locale === this.config.defaultLocale ? '' : `/${locale}`;
    }

    private getCurrentPath() {
        if (isPlatformServer(this.platformId)) {
            const req: Request = this.injector.get(REQUEST);
            return req.originalUrl;
        } else {
            return window.location.pathname;
        }
    }

    private setGlobalBasePathVariable(value) {
        if (typeof window !== 'undefined') {
            window['__BASE'] = value;
        } else {
            global['__BASE'] = value;
        }
    }

    public initialize(config: WrikeDynamicBaseHrefModuleConfiguration, translateService: TranslateService): void {
        this.config = config || ({} as WrikeDynamicBaseHrefModuleConfiguration);
        this.translateService = translateService;
    }
}
